<template>
  <div :id="mapId" ref="target" class="w-full h-full bg-theme-30"/>
  <template v-if="map">
    <div v-for="marker in locationMarkers" :key="`${marker.latitude},${marker.longitude}`">
      <ClientOnly>
        <MapMarker
          :marker="marker"
          :map="mapProp"
          :hovered="!!marker.locations?.find(location => location.id === hoveredLocationId)"
          :selected="!!marker.locations?.find(location => location.id === selectedLocationId)"
          :readonly="readonly"
          @click="flyToMarker(marker)"
          @click-location="(event, location) => emit('click-location', event, location)" />
      </ClientOnly>
    </div>
  </template>
</template>

<script setup lang="ts" name="Map">
import type { Location } from '@corporate/components/LocationCard/LocationCard.vue'
import type { Domain } from '@gro-up/types'
import { useResponsive } from '@gro-up/ui/composables'
import { useIntersectionObserver } from '@vueuse/core'
import type { Map as MapboxMap, LngLatBounds } from 'mapbox-gl'
import { useId, ref, computed } from 'vue'

import MapMarker, { type LocationMarker } from './MapMarker.vue'

const props = defineProps<{
  accessToken: string
  initialCenter: [number, number]
  initialZoom: number
  bounds?: LngLatBounds | null
  locations?: Location[]
  hoveredLocationId?: string | null
  selectedLocationId?: string | null
  domain?: Domain
  readonly?: boolean
}>()

const emit = defineEmits(['marker-click', 'click-location', 'init', 'moveend'])

const mapId = useId()
const { isMaxLg } = useResponsive()

const map = ref<MapboxMap | null>(null)
const target = ref(null)

const { stop } = useIntersectionObserver(target, ([entry]) => {
  if (entry?.isIntersecting) {
    initialize()
    stop()
  }
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapProp = computed(() => map.value as any)

const initialize = async () => {
  await import('mapbox-gl/dist/mapbox-gl.css')
  const { Map, NavigationControl, GeolocateControl } = await import('mapbox-gl')

  map.value = new Map({
    accessToken: props.accessToken,
    container: mapId,
    style: 'mapbox://styles/gro-up-2025/cm9h5z9yg00qi01s44028bfk1',
    center: props.initialCenter,
    zoom: props.initialZoom,
    bounds: props.bounds ?? undefined,
    language: 'nl',
    minZoom: 7,
  })

  if (props.bounds) {
    map.value.fitBounds(props.bounds)
  }

  map.value.addControl(new NavigationControl())
  map.value.addControl(new GeolocateControl({ fitBoundsOptions: { maxZoom: 14 } }))

  map.value.on('moveend', () => {
    if (map.value) {
      emit('moveend', map.value)
    }
  })

  emit('init', map.value)
}

const updatePosition = (position: { latitude: number; longitude: number; radius: number }, duration = 500) => {
  const getMapboxZoomByRadius = (radius: number, mapWidth: number): number => {
    const EARTH_CIRCUMFERENCE_KM = 40075
    const MAPBOX_TILE_SIZE = 512
    const WORLD_MAP_SIZE = MAPBOX_TILE_SIZE * Math.pow(2, 0)
    const widthFraction = mapWidth / WORLD_MAP_SIZE
    const zoom = Math.log2((EARTH_CIRCUMFERENCE_KM * widthFraction) / (radius * 2))
    return Math.max(0, Math.min(22, zoom))
  }

  map.value?.easeTo({
    center: [position.longitude, position.latitude],
    zoom: getMapboxZoomByRadius(position.radius, map.value?.getCanvas().getBoundingClientRect().width ?? 0),
    duration,
  })
}

const flyToMarker = (marker: LocationMarker) => {
  emit('marker-click', marker)

  if (!map.value) {
    return
  }

  const offset = (isMaxLg.value ? [0, map.value.getCanvas().getBoundingClientRect().height * 0.4] : [0, 0]) as [
    number,
    number,
  ]

  map.value.easeTo({
    center: [marker.longitude, marker.latitude],
    offset,
    duration: 500,
  })
}

const locationMarkers = computed(() => {
  const markers: Record<string, LocationMarker> = {}

  if (props.locations) {
    for (const location of props.locations) {
      if (!location.position) {
        continue
      }

      const key = `${location.position.latitude},${location.position.longitude}`
      if (!markers[key]) {
        markers[key] = {
          latitude: location.position.latitude,
          longitude: location.position.longitude,
          domain: location.domain,
          locations: [location],
        }
      } else {
        markers[key].locations?.push(location)
      }
    }
  }

  return Object.values(markers)
})

const fitBounds = (bounds: LngLatBounds) => {
  map.value?.fitBounds(bounds, { padding: 50, duration: 500 })
}

defineExpose({
  updatePosition,
  fitBounds,
})
</script>

<style>
@media (max-width: 64rem) {
  .mapboxgl-ctrl-bottom-right,
  .mapboxgl-ctrl-bottom-left {
    margin-bottom: 10px;
  }
}
</style>
